/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import {
  CalendarPlusIcon,
  ChatCircleIcon,
  ChatTextIcon,
  CheckCircleIcon,
  ClipboardTextIcon,
  PhoneIcon,
  VoicemailIcon,
} from '@phosphor-icons/react'
import { useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { INBOX_TAB } from 'app/components/contacts/ContactRelatedObjects'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from 'app/components/ui/dropdown-menu'
import { cn } from 'app/lib/utils'
import { ContactJson } from 'app/types/entities'
import { useHandleSearchParams } from 'app/utils'
import { readLocationIds } from './locationParam'
import { ResponseChannel, groupByChannel, nextAfterRemoval } from './responseQueue'
import { ResponseRecordModal } from './ResponseRecordModal'
import { Row, displayName, stamp } from './ResponseRow'
import { ResponseRow, useResponseQueue } from './useResponseQueue'

const CHANNEL_META: Record<ResponseChannel, { icon: React.ReactElement; tint: string }> = {
  sms: { icon: <ChatTextIcon size={14} weight="fill" />, tint: `text-blue-600` },
  call: { icon: <PhoneIcon size={14} weight="fill" />, tint: `text-amber-600` },
  voicemail: { icon: <VoicemailIcon size={14} weight="fill" />, tint: `text-purple-600` },
  form: { icon: <ClipboardTextIcon size={14} weight="fill" />, tint: `text-sky-600` },
  chat: { icon: <ChatCircleIcon size={14} weight="fill" />, tint: `text-minimal` },
  scheduler: { icon: <CalendarPlusIcon size={14} weight="fill" />, tint: `text-green-600` },
}

const COMMUNICATION_PARAM = `communication`

export function ResponseQueuePanel({ locationTerm }: { locationTerm?: string }) {
  const [searchParams] = useSearchParams()
  // The page's Locations control encodes into `filters`; the queue obeys it
  // like every other figure on the page.
  const locationIds = useMemo(() => readLocationIds(searchParams.get(`filters`)), [searchParams])
  const scope =
    locationTerm ?? (locationIds.length ? `locationId:(${locationIds.map((id) => `"${id}"`).join(` OR `)})` : undefined)

  const { rows, loading, snooze, dismiss, markRead } = useResponseQueue(scope)
  const [openContact, setOpenContact] = useState<ContactJson>()
  const [openTab, setOpenTab] = useState<string>()
  const handleSearchParams = useHandleSearchParams()
  // The open record lives in the URL so a copied link reopens it. Nothing is
  // selected on arrival otherwise: landing on the first row would mark its
  // thread read before anyone looked, and reading is not answering.
  const selectedId = searchParams.get(COMMUNICATION_PARAM) ?? undefined

  const waiting = rows.filter((row) => !row.handled)
  const handled = rows.filter((row) => row.handled)
  const groups = groupByChannel(waiting)
  const ordered = groups.flatMap((group) => group.items)

  const clearAndAdvance = (row: ResponseRow, act: () => void) => {
    handleSearchParams({ [COMMUNICATION_PARAM]: nextAfterRemoval(ordered, row.id) }, { replace: true })
    act()
  }

  // Resolved from the live rows, and guarded on `selectedId` being set at all,
  // so an empty selection never matches a row.
  const selectedRow = selectedId ? waiting.find((row) => row.id === selectedId) : undefined

  // Only the first load gets a skeleton; the poll sets `loading` again every
  // thirty seconds.
  const settled = useRef(false)
  if (!loading) settled.current = true

  // Rendered beside the empty state too: answering the last person empties the
  // list, and a drawer that closed at that moment would vanish mid-read.
  const drawer = (
    <ContactDrawer
      openDrawer={Boolean(openContact)}
      contact={openContact}
      onClose={() => {
        setOpenContact(undefined)
        setOpenTab(undefined)
      }}
      initialTab={openTab}
      // Edits made in the drawer stay on screen; the row catches up on the next poll.
      onChange={setOpenContact}
    />
  )

  const heading = (waitingCount?: number) => (
    <>
      <div className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-ui-sm text-strong">
            <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" />
            Communications that might need your attention
          </p>
          <p className="mt-0.5 hidden text-body-xs text-subtle md:block">
            Calls, voicemails and forms from today and yesterday. Texts stay until they are answered.
          </p>
        </div>
        <div className="flex shrink-0 items-baseline gap-2 text-body-xs text-subtle">
          {handled.length > 0 && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="rounded text-body-xs text-green-700 transition hover:text-green-800 hover:underline"
                  >
                    {handled.length} marked as done
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 p-0">
                  <p className="border-b border-subtle px-3 py-2 text-body-xs uppercase tracking-wide text-subtle">
                    Marked as done
                  </p>
                  <div className="max-h-64 overflow-y-auto py-1">
                    {handled.map((row) => {
                      // A PMS booking names nobody, so say what happened instead.
                      const by = row.handled?.type === `Appointment` ? `Scheduled` : row.handled?.actor
                      return (
                        <div
                          key={row.id}
                          className="flex items-center justify-between gap-3 px-3 py-1.5 text-body-xs text-subtle"
                        >
                          <span className="truncate text-strong">{displayName(row)}</span>
                          <span className="shrink-0 tabular-nums">
                            {by ? `${by}, ` : ``}
                            {stamp(row.handled?.at ?? row.latest)}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              <span className="text-minimal">·</span>
            </>
          )}
          {waitingCount != null && (
            <p>
              <span className="tabular-nums text-strong">{waitingCount}</span> waiting
            </p>
          )}
        </div>
      </div>
      <div className="mt-3 h-px bg-gradient-to-r from-amber-500/30 to-transparent" />
    </>
  )

  if (loading && !settled.current) {
    return (
      <section className="mt-6">
        {heading()}
        <div className="mt-5 overflow-hidden rounded-xl border border-default bg-surface-raised shadow-elevation-2">
          <div className="flex h-10 items-center gap-2 border-b border-subtle bg-surface-subtle px-4">
            <span className="h-3.5 w-3.5 shrink-0 animate-pulse rounded bg-surface-muted" />
            <span className="h-2.5 w-24 animate-pulse rounded bg-surface-muted" />
          </div>
          {[0, 1, 2].map((row) => (
            <div key={row} className="flex items-center gap-3 border-b border-subtle px-4 py-3 last:border-b-0">
              <span className="h-7 w-7 shrink-0 animate-pulse rounded-full bg-surface-muted" />
              <span className="min-w-0 flex-1">
                <span className="block h-2.5 w-1/3 animate-pulse rounded bg-surface-muted" />
                <span className="mt-1.5 block h-2 w-2/3 animate-pulse rounded bg-surface-muted" />
              </span>
              <span className="h-2.5 w-12 shrink-0 animate-pulse rounded bg-surface-muted" />
            </div>
          ))}
        </div>
      </section>
    )
  }
  if (waiting.length === 0 && handled.length === 0) {
    return (
      <>
        <section className="mt-6">
          {heading()}
          <div className="mt-5 rounded-xl border border-default bg-surface-raised px-4 py-6 text-center shadow-elevation-1">
            <p className="inline-flex items-center gap-2 text-ui-sm text-green-700">
              <CheckCircleIcon size={18} weight="fill" /> Nobody is waiting on a reply
            </p>
            <p className="mt-1 text-body-xs text-subtle">
              Every text has been answered, and so has every call, form and chat from yesterday and today.
            </p>
          </div>
        </section>
        {drawer}
      </>
    )
  }

  return (
    <>
      <section className="mt-6">
        {heading(waiting.length)}
        <div className="mt-5 flex flex-col gap-4">
          {groups.map((group) => (
            <div
              key={group.channel}
              className="overflow-hidden rounded-xl border border-default bg-surface-raised shadow-elevation-2"
            >
              <div className="flex h-10 items-center gap-2 border-b border-subtle bg-surface-subtle px-4">
                <span className={cn(`flex shrink-0 items-center`, CHANNEL_META[group.channel].tint)}>
                  {CHANNEL_META[group.channel].icon}
                </span>
                <span className="text-label-xs uppercase tracking-wide text-subtle">{group.label}</span>
                <span className="text-body-xs tabular-nums text-minimal">{group.items.length}</span>
              </div>
              <div className="max-h-[19rem] overflow-y-auto">
                {group.items.map((row) => (
                  // Keyed by `row.id`, not by `row.key`: that key is the id of
                  // the oldest unanswered touch and moves as touches arrive,
                  // which would drop the selection on the next poll.
                  <Row
                    key={row.id}
                    row={row}
                    selected={!!selectedId && selectedId === row.id}
                    onSelect={() => handleSearchParams({ [COMMUNICATION_PARAM]: row.id }, { replace: true })}
                    onDone={() => clearAndAdvance(row, () => dismiss(row))}
                    onOpen={() => {
                      if (!row.contact) return
                      setOpenTab(row.channel === `sms` ? INBOX_TAB : undefined)
                      setOpenContact(row.contact)
                    }}
                    onSnooze={(until) => clearAndAdvance(row, () => snooze(row, until))}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <ResponseRecordModal
        row={selectedRow}
        ordered={ordered}
        onClose={() => handleSearchParams({ [COMMUNICATION_PARAM]: undefined }, { replace: true })}
        onGo={(id) => handleSearchParams({ [COMMUNICATION_PARAM]: id }, { replace: true })}
        onDone={(row) => clearAndAdvance(row, () => dismiss(row))}
        onSnooze={(row, until) => clearAndAdvance(row, () => snooze(row, until))}
        onRead={markRead}
        onOpenContact={(row) => {
          if (!row.contact) return
          handleSearchParams({ [COMMUNICATION_PARAM]: undefined }, { replace: true })
          setOpenTab(row.channel === `sms` ? INBOX_TAB : undefined)
          setOpenContact(row.contact)
        }}
      />
      {drawer}
    </>
  )
}
